<script setup>
import { computed, ref } from 'vue'
import { useFilingDetailStore } from '../../stores/filingDetailStore'
import { useRoute, useRouter } from 'vue-router'
import BModal from '../bootstrap/BModal.vue'
import BeneficialOwnersCardItem from './BeneficialOwnersCardItem.vue'
import StatusBadge from './StatusBadge.vue'

defineProps({
  owners: Array
})

const filingDetailStore = useFilingDetailStore()
const route = useRoute()
const router = useRouter()

const showDeleteModal = ref(false)
const ownerToDelete = ref(null)

// Compute the path for adding a new owner
const addOwnerPath = computed(() => {
  return `/a/${route.params.teamSlug}/filings/${route.params.filingId}/add-owner/`
})

// Function to handle button click
const navigateToAddOwner = () => {
  router.push(addOwnerPath.value)
}

// Function to handle edit button click
const editOwner = (ownerId) => {
  router
    .push(
      `/a/${route.params.teamSlug}/filings/${route.params.filingId}/edit-owner/${ownerId}`
    )
    .catch((err) => {
      console.error('Navigation error:', err)
    })
}

const openDeleteModal = (ownerId) => {
  ownerToDelete.value = ownerId
  showDeleteModal.value = true
}

const confirmDelete = () => {
  if (ownerToDelete.value) {
    filingDetailStore.removeBeneficialOwnerFromFiling(ownerToDelete.value)
    showDeleteModal.value = false
    ownerToDelete.value = null
  }
}
</script>

<template>
  <div class="card mb-3">
    <div class="card-header border-bottom pb-3">
      <h3 class="card-title">Beneficial Owners</h3>
      <StatusBadge
        :isComplete="filingDetailStore.allOwnersComplete"
        incompleteMsg="Incomplete"
        completeMsg="If All Owners Present, Ready to Submit"
      />
    </div>
    <div v-editable class="alert alert-soft-secondary card-alert" role="alert">
      Please add all of the companies Beneficial Owners. A Beneficial Owner is
      any individual who directly or indirectly:
      <ul class="text-start">
        <li>Excercises substantial control over the company</li>
        <li>Owns or controls at least 25% or more of the company</li>
      </ul>
    </div>
    <div class="card-body">
      <!-- Cards for Owners -->
      <div class="row gx-3 gy-2 align-items-center">
        <div v-if="owners.length >= 1">
          <BeneficialOwnersCardItem
            v-for="owner in owners"
            :key="owner.id"
            :owner="owner"
            @edit="editOwner"
            @delete="openDeleteModal"
          />
        </div>
        <div class="row justify-center" v-if="owners.length <= 0">
          <!-- Card -->
          <div class="card card-ghost card-centered h-100" href="#">
            <div class="card-body">
              <div class="mb-2 text-primary">
                <i class="fa-duotone fa-solid fa-user-plus fa-2xl"></i>
              </div>
              <span class="text-primary text-muted fs-3"
                >Add Your First Beneficial Owner</span
              >
            </div>
          </div>
          <!-- End Card -->
        </div>
        <button
          @click="navigateToAddOwner"
          v-editable
          type="button"
          class="btn btn-outline-primary btn-lg"
        >
          <i class="fa-light fa-user-plus" aria-hidden="true"></i> Add
        </button>
      </div>
    </div>
  </div>
  <BModal
    id="deleteOwnerModal"
    title="Confirm Deletion"
    ok-title="Delete"
    :visible="showDeleteModal"
    @update:visible="showDeleteModal = $event"
    @ok="confirmDelete"
  >
    <p class="p-lead">
      Are you sure you want to delete this Beneficial Owner? This action cannot
      be undone.
    </p>
  </BModal>
</template>
